import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '../states/store';
import overlayBullish from '../assets/images/overlays/buol.png';
import overlayBearish from '../assets/images/overlays/bdol.png';
import overlayBearishBullish from '../assets/images/overlays/bvb-1.png';

function ResultSummaryPanel() {
    const { timerStatus, currentRound, authUser, currentUpBets, currentDownBets, totalUpBets, totalDownBets, currentUpWager, currentDownWager, resultsData } = useStore();
    const [drawResult, setDrawResult] = useState(resultsData && resultsData.result ? resultsData.result.toLowerCase() : null);
    const drawResults = resultsData && resultsData.result ? resultsData.result.toLowerCase() : null;
    const [isHovering, setIsHovering] = useState(false);
    const [animatedValue, setAnimatedValue] = useState(0);

    useEffect(() => {
        // Simple animation for percentage change
        if (resultsData?.result_percentage_change) {
            setAnimatedValue(0);
            const timer = setTimeout(() => {
                setAnimatedValue(resultsData.result_percentage_change);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [resultsData]);

    // Determine result type and styles
    const resultType = drawResults === 'up' ? 'up' : drawResults === 'down' ? 'down' : 'draw';
    
    const resultConfig = {
        up: {
            primary: 'from-emerald-500 to-teal-600',
            secondary: 'from-emerald-400 to-teal-500',
            accent: 'emerald',
            text: 'text-emerald-400',
            bg: 'bg-emerald-500/10',
            border: 'border-emerald-500/30',
            shadow: 'shadow-emerald-500/20',
            gradient: 'from-emerald-900/80 to-teal-900/80',
            lightGradient: 'from-emerald-500/20 to-teal-500/20',
            label: 'UP',
            labelKr: '업',
            icon: (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
            ),
        },
        down: {
            primary: 'from-rose-500 to-pink-600',
            secondary: 'from-rose-400 to-pink-500',
            accent: 'rose',
            text: 'text-rose-400',
            bg: 'bg-rose-500/10',
            border: 'border-rose-500/30',
            shadow: 'shadow-rose-500/20',
            gradient: 'from-rose-900/80 to-pink-900/80',
            lightGradient: 'from-rose-500/20 to-pink-500/20',
            label: 'DOWN',
            labelKr: '다운',
            icon: (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
            ),
        },
        draw: {
            primary: 'from-amber-500 to-orange-600',
            secondary: 'from-amber-400 to-orange-500',
            accent: 'amber',
            text: 'text-amber-400',
            bg: 'bg-amber-500/10',
            border: 'border-amber-500/30',
            shadow: 'shadow-amber-500/20',
            gradient: 'from-amber-900/80 to-orange-900/80',
            lightGradient: 'from-amber-500/20 to-orange-500/20',
            label: 'DRAW',
            labelKr: '무승부',
            icon: (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7h12m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
            ),
        },
    };

    const currentConfig = resultConfig[resultType];

    // Format percentage change with sign
    const percentageChange = resultsData?.result_percentage_change;
    const percentageColor = percentageChange > 0 ? 'text-emerald-400' : percentageChange < 0 ? 'text-rose-400' : 'text-slate-400';
    const percentageSign = percentageChange > 0 ? '+' : '';

    // Calculate win rates
    const totalBets = (totalUpBets || 0) + (totalDownBets || 0);
    const upWinRate = totalBets > 0 ? Math.round((totalUpBets / totalBets) * 100) : 0;
    const downWinRate = totalBets > 0 ? Math.round((totalDownBets / totalBets) * 100) : 0;

    return (
        <div 
            className="relative bg-slate-900 w-full font-bold text-shadow-lg/30 overflow-hidden rounded-xl border border-white/10 shadow-xl transition-all duration-300 hover:shadow-2xl"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            {/* Animated gradient background */}
            <div className={`absolute inset-0 bg-gradient-to-br ${currentConfig.gradient} transition-opacity duration-700 ${isHovering ? 'opacity-100' : 'opacity-80'}`}></div>
            
            {/* Light overlay gradient for depth */}
            <div className={`absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30 pointer-events-none`} />
            
            {/* Image Overlay with enhanced effects */}
            <div 
                className="absolute inset-0 pointer-events-none transition-all duration-1000"
                style={{
                    backgroundImage: `url(${drawResults === 'up' ? overlayBullish : drawResults === 'down' ? overlayBearish : overlayBearishBullish})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    opacity: isHovering ? 0.20 : 0.10,
                    //transform: isHovering ? 'scale(1.05)' : 'scale(1)',
                    filter: isHovering ? 'brightness(1.2)' : 'brightness(1)',
                }}
            />
            
            {/* Content */}
            <div className="relative z-10 text-slate-300">
                {/* Header with result badge - Enhanced */}
                <div className="flex items-center justify-between px-3 pt-2 pb-1 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <div className={`w-1 h-4 rounded-full bg-gradient-to-b ${currentConfig.primary}`} />
                        <span className='text-xs font-medium text-white/70 tracking-wider'>ROUND RESULT</span>
                    </div>
                    {drawResults && (
                        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gradient-to-r ${currentConfig.primary} shadow-lg shadow-${currentConfig.accent}-500/30`}>
                            {currentConfig.icon}
                            <span className="text-xs font-bold text-white">{currentConfig.label}</span>
                        </div>
                    )}
                </div>

                {/* Main Content Grid */}
                <div className='flex flex-row text-xs p-2 gap-1'>
                    {/* Left Column */}
                    <div className='w-1/2 flex flex-col'>
                        {/* Result row with animated indicator */}
                        <div className='flex flex-row p-1.5 hover:bg-white/5 rounded-lg transition-all duration-200 group'>
                            <span className='w-1/2 text-white/50 group-hover:text-white/70 transition-colors'>결과</span>
                            <div className={`w-1/2 text-right font-bold ${currentConfig.text} flex items-center justify-end gap-1.5`}>
                                <span className="relative">
                                    {currentConfig.labelKr}
                                    <span className={`absolute -bottom-0.5 left-0 w-full h-0.5 bg-current scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`} />
                                </span>
                                <span className="relative flex h-2 w-2">
                                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${currentConfig.bg} opacity-75`}></span>
                                    <span className={`relative inline-flex rounded-full h-2 w-2 ${currentConfig.bg}`}></span>
                                </span>
                            </div>
                        </div>
                        
                        {/* Percentage change with animated counter */}
                        <div className='flex flex-row p-1.5 hover:bg-white/5 rounded-lg transition-all duration-200 group'>
                            <span className='w-1/2 text-white/50 group-hover:text-white/70 transition-colors'>요율 변화</span>
                            <span className={`w-1/2 text-right font-medium ${percentageColor} group-hover:scale-105 transition-transform`}>
                                <span className="inline-block animate-pulse-subtle">
                                    {percentageSign}{percentageChange}%
                                </span>
                            </span>
                        </div>

                        {/* Up bets with micro progress bar */}
                        <div className='flex flex-col p-1.5 hover:bg-white/5 rounded-lg transition-all duration-200 group'>
                            <div className='flex flex-row w-full'>
                                <span className='w-1/2 text-white/50 group-hover:text-white/70 transition-colors'>업</span>
                                <span className='w-1/2 text-right font-bold text-emerald-400 flex items-center justify-end gap-1'>
                                    {totalUpBets?.toLocaleString()}
                                    <span className="text-[10px] text-emerald-500/70">({upWinRate}%)</span>
                                </span>
                            </div>
                            {/* Mini progress bar */}
                            <div className="w-full h-0.5 bg-white/5 rounded-full mt-1 overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full transition-all duration-500"
                                    style={{ width: `${upWinRate}%` }}
                                />
                            </div>
                        </div>

                        {/* Up winners */}
                        <div className='flex flex-row p-1.5 hover:bg-white/5 rounded-lg transition-all duration-200 group'>
                            <span className='w-1/2 text-white/50 group-hover:text-white/70 transition-colors'>업 당첨</span>
                            <span className='w-1/2 text-right font-bold text-emerald-400 flex items-center justify-end gap-1'>
                                {currentUpWager?.toLocaleString()}
                                <svg className="w-3 h-3 text-emerald-500/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                        </div>
                    </div>

                    {/* Vertical Divider with gradient */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent" />
                    </div>

                    {/* Right Column */}
                    <div className='w-1/2 flex flex-col pl-1'>
                        {/* Start price with icon */}
                        <div className='flex flex-row p-1.5 hover:bg-white/5 rounded-lg transition-all duration-200 group'>
                            <span className='w-1/2 text-white/50 group-hover:text-white/70 transition-colors'>시작가</span>
                            <div className={"w-1/2 text-right font-bold text-white flex items-center justify-end gap-1"}>
                                <span className="text-[10px] text-white/40">₩</span>
                                {resultsData?.draw_open_price?.toLocaleString()}
                            </div>
                        </div>
                        
                        {/* End price with icon */}
                        <div className='flex flex-row p-1.5 hover:bg-white/5 rounded-lg transition-all duration-200 group'>
                            <span className='w-1/2 text-white/50 group-hover:text-white/70 transition-colors'>종료가</span>
                            <span className='w-1/2 text-right font-bold text-white flex items-center justify-end gap-1'>
                                <span className="text-[10px] text-white/40">₩</span>
                                {resultsData?.draw_close_price?.toLocaleString()}
                            </span>
                        </div>

                        {/* Down bets with micro progress bar */}
                        <div className='flex flex-col p-1.5 hover:bg-white/5 rounded-lg transition-all duration-200 group'>
                            <div className='flex flex-row w-full'>
                                <span className='w-1/2 text-white/50 group-hover:text-white/70 transition-colors'>다운</span>
                                <span className='w-1/2 text-right font-bold text-rose-400 flex items-center justify-end gap-1'>
                                    {totalDownBets?.toLocaleString()}
                                    <span className="text-[10px] text-rose-500/70">({downWinRate}%)</span>
                                </span>
                            </div>
                            {/* Mini progress bar */}
                            <div className="w-full h-0.5 bg-white/5 rounded-full mt-1 overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-rose-400 to-pink-400 rounded-full transition-all duration-500"
                                    style={{ width: `${downWinRate}%` }}
                                />
                            </div>
                        </div>

                        {/* Down winners */}
                        <div className='flex flex-row p-1.5 hover:bg-white/5 rounded-lg transition-all duration-200 group'>
                            <span className='w-1/2 text-white/50 group-hover:text-white/70 transition-colors'>다운 당첨</span>
                            <span className='w-1/2 text-right font-bold text-rose-400 flex items-center justify-end gap-1'>
                                {currentDownWager?.toLocaleString()}
                                <svg className="w-3 h-3 text-rose-500/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Footer with distribution bar */}
                {totalBets > 0 && (
                    <div className="px-3 pb-2 pt-1 border-t border-white/10">
                        <div className="flex items-center justify-between text-[10px] text-white/40 mb-1">
                            <span>Distribution</span>
                            <div className="flex gap-2">
                                <span className="text-emerald-400">UP {upWinRate}%</span>
                                <span className="text-rose-400">DOWN {downWinRate}%</span>
                            </div>
                        </div>
                        <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-gradient-to-r from-emerald-400 via-teal-400 to-rose-400 rounded-full transition-all duration-700 relative"
                                style={{ 
                                    width: '100%',
                                    background: `linear-gradient(90deg, #10b981 0%, #10b981 ${upWinRate}%, #f43f5e ${upWinRate}%, #f43f5e 100%)`
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Enhanced glow effects */}
                <div className={`absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br ${currentConfig.primary} rounded-full blur-2xl opacity-20 pointer-events-none animate-pulse`} style={{ animationDuration: '3s' }} />
                <div className={`absolute -bottom-10 -left-10 w-20 h-20 bg-gradient-to-br ${currentConfig.primary} rounded-full blur-2xl opacity-20 pointer-events-none animate-pulse`} style={{ animationDuration: '4s' }} />
                
                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-white/10 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-white/10 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-white/10 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-white/10 rounded-br-lg" />
            </div>  
        </div>
    );
}

// Add custom animation to tailwind config or use style tag
const style = document.createElement('style');
style.textContent = `
    @keyframes pulse-subtle {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.8; }
    }
    .animate-pulse-subtle {
        animation: pulse-subtle 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
`;
document.head.appendChild(style);

export default ResultSummaryPanel;